<script lang="ts" setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";
import type { RecentlyViewedItem } from "@/types";
import DashboardHeader from "@/components/DashboardHeader.vue";

const router = useRouter();
const {
  recentItems,
  removeItem,
  clearAll,
  getTypeLabel,
  getBadgeClassForType,
  formatTimeAgo,
} = useRecentlyViewed();

// Server caps the list at UserRecentView.MaxItemsPerOrg (10); render whatever it returns.
const displayItems = computed(() => recentItems());

function navigateTo(item: RecentlyViewedItem) {
  router.push(item.route);
}

function handleRemoveItem(item: RecentlyViewedItem, event: Event) {
  event.stopPropagation(); // Prevent navigation when clicking remove
  removeItem(item.id, item.type);
}

function handleClearAll() {
  if (
    confirm(
      "Are you sure you want to clear all recently viewed items? This action cannot be undone."
    )
  ) {
    clearAll();
  }
}
</script>

<template>
  <div class="recently-viewed">
    
    <DashboardHeader :title="'Recently Viewed'" >
      <template #actions>
        <button
            v-if="displayItems.length > 0"
            class="bb-btn-danger btn-sm"
            @click="handleClearAll"
            title="Clear all recently viewed items"
        ><i class="bi bi-trash"></i>
          Clear
        </button>
      </template>
    </DashboardHeader>
    

    <div class="recent-items-container scrollable-container">
      <div
        v-if="displayItems.length === 0"
        class="empty-state text-center text-muted d-flex flex-column justify-content-center align-items-center flex-grow-1"
      >
        <i class="bi bi-clock-history fs-1 d-block mb-3"></i>
        <p class="mb-0">No recently viewed items</p>
        <small class="text-muted">
          Items you visit will appear here for quick access
        </small>
      </div>

      <div
        v-for="item in displayItems"
        :key="`${item.type}-${item.id}`"
        class="recent-item"
        @click="navigateTo(item)"
      >
        <div class="recent-item-icon">
          <i :class="`bi ${item.icon}`"></i>
        </div>
        <div class="recent-item-content">
          <div class="d-flex align-items-start justify-content-between mb-1">
            <p class="mb-0 recent-item-name">{{ item.name }}</p>
            <span
              :class="`badge ${getBadgeClassForType(item.type)} ms-2 flex-shrink-0`"
            >
              {{ getTypeLabel(item.type) }}
            </span>
          </div>
          <p v-if="item.description" class="recent-item-description mb-1">
            {{ item.description }}
          </p>
          <div class="d-flex align-items-center justify-content-between">
            <small class="text-muted">
              <i class="bi bi-clock me-1"></i>
              {{ formatTimeAgo(item.lastViewed) }}
            </small>
            <button
              class="bb-btn-danger btn-sm remove-btn"
              @click="(e) => handleRemoveItem(item, e)"
              title="Remove from recent items"
            >
              <i class="bi bi-x-circle"></i> Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.recently-viewed {
  .title {
    margin-bottom: 1rem;

    h3 {
      margin: 0;
    }
  }

  .recent-items-container {
    min-height: 40vh;
    max-height: 40vh;
    padding-right: 0.5rem;
    display: flex;
    flex-direction: column;
  }

  .recent-item {
    display: flex;
    align-items: start;
    gap: 1rem;
    padding: 0.875rem;
    margin-bottom: 0.75rem;
    background: var(--bs-tertiary-bg);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    border: 1px solid transparent;
    position: relative;

    &:hover {
      background: var(--bs-secondary-bg);
      border-color: var(--bs-primary);

      .remove-btn {
        opacity: 1;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }

    .recent-item-icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bs-body-bg);
      border-radius: 0.375rem;
      color: var(--bs-primary);
      font-size: 1.25rem;
    }

    .recent-item-content {
      flex: 1;
      min-width: 0;

      .recent-item-name {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--bs-emphasis-color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .recent-item-description {
        font-size: 0.8125rem;
        color: var(--bs-secondary-color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      small {
        font-size: 0.75rem;
      }

      .remove-btn {
        opacity: 0;
        transition: opacity 0.2s ease;
        font-size: 1rem;
        line-height: 1;

        &:hover {
          opacity: 1;
        }
      }
    }
  }
}
</style>
